import React, { useEffect, useState } from 'react';
import { Button, Card, Typography, Dropdown, Menu, Row, Col, Drawer, Empty, Modal, message, Tooltip, Spin } from 'antd'
import { listPrescriptions } from "../../services/prescription.service"
import moment from 'moment'
import { PageHeader } from '../pageHeader/pageHeader.component';
import { useNavigate } from "react-router-dom";
import { TrackingHelper } from "../../helpers/tracking.helper.js"
import { PlusOutlined, DownOutlined, HistoryOutlined, InfoCircleOutlined, WarningOutlined, AlertOutlined, LinkOutlined, SafetyOutlined, InboxOutlined, ExclamationCircleOutlined, MedicineBoxOutlined, SettingOutlined, StopOutlined, ReloadOutlined } from '@ant-design/icons';
import "./prescriptions.scss"
import PrescriptionStatus from '../../enums/prescriptionStatus.enum.js';
import { PrescriptionData } from '../../data/prescription.data.js';
import { cancelPrescription } from '../../services/prescription.service';
import ShippingHelper  from '../../helpers/shipping.helper.js';
import { startCase, toLower } from 'lodash';

const { Text } = Typography

export const Prescriptions = () => {
  const [prescriptions, setPrescriptions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedMedication, setSelectedMedication] = useState(null)
  const [isMedicationHistoryDrawerVisible, setIsMedicationHistoryDrawerVisible] = useState(false)
  const [isInfoDrawerVisible, setIsInfoDrawerVisible] = useState(false)
  const [isStopModalVisible, setIsStopModalVisible] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    const fetchPrescriptions = async () => {
      const response = await listPrescriptions({
        populate: {
          path: 'pharmacy',
          select: 'name'
        }
      })
      setPrescriptions(response)
      setIsLoading(false)
    }
    document.title = 'Prescriptions | Instalab'
    fetchPrescriptions()
  }, [])

  // Group prescriptions by medication type
  const groupedPrescriptions = prescriptions.reduce((acc, prescription) => {
    const type = prescription.contents?.type || 'Unknown';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(prescription);
    return acc;
  }, {});

  

  const MedicationCard = ({ type, prescriptions }) => {
    // Sort prescriptions by date to get earliest and latest
    const sortedPrescriptions = [...prescriptions].sort((a, b) => 
      new Date(a.createdAt) - new Date(b.createdAt)
    );

    const earliestPrescription = sortedPrescriptions[0];
    const latestPrescription = sortedPrescriptions[sortedPrescriptions.length - 1];
    const isPaused = latestPrescription.status === PrescriptionStatus.PAUSED;

    const managementMenu = {
      items: [
        {
          key: 'stop',
          icon: <StopOutlined />,
          label: 'Stop Medication',
          onClick: () => {
            setSelectedMedication({ 
              type, 
              prescriptions: sortedPrescriptions
            });
            setIsStopModalVisible(true);
          }
        },

        // {
        //   key: 'restart',
        //   icon: <ReloadOutlined />,
        //   label: 'Restart Medication',
        //   onClick: () => {
        //     // Add your restart medication logic here
        //     console.log('Restart medication:', type);
        //   }
        // }
      ]
    };

    return (
      <>
        <Card 
          className={`medication-card flat ${isPaused ? 'paused' : ''}`}
          actions={[
            <Button 
            type="link" 
            className='history-button'
            icon={<InfoCircleOutlined />}
            onClick={() => {
              setSelectedMedication({ type, prescriptions: sortedPrescriptions });
              setIsInfoDrawerVisible(true);
            }}
            >
             About
          </Button>,
            <Button 
              type="link" 
              className='history-button'
              icon={<HistoryOutlined />}
              onClick={() => {
                setSelectedMedication({ type, prescriptions: sortedPrescriptions });
                setIsMedicationHistoryDrawerVisible(true);
              }}
            >
              History
            </Button>,  

            latestPrescription.status !== PrescriptionStatus.PAUSED && <Dropdown menu={managementMenu} trigger={['click']}>
              <span>
                <Button 
                  type="link" 
                  className='history-button'
                  icon={<SettingOutlined />}
                >
                  Manage
                </Button>
              </span>
            </Dropdown>
          ].filter(Boolean)}
        >
          <div className="medication-card-content">
            <div className="medication-card-row">
              <Text className="medication-name">
                {type}
              </Text>
            </div>

            <div className="medication-card-row">
              {latestPrescription.contents?.sig}
            </div>
   


            <div className="medication-card-row">
              Status: {latestPrescription.status === PrescriptionStatus.PAUSED ? 'Stopped' : latestPrescription.status === PrescriptionStatus.PENDING ? 'Pending Physician Approval' : 'Active'}
            </div>

            {latestPrescription.status !== PrescriptionStatus.PENDING && (
              <div className="medication-card-row">
                Started: {moment(earliestPrescription.orderedAt || earliestPrescription.createdAt).format('MM/DD/YYYY')}
              </div>
            )}

            {latestPrescription.status !== PrescriptionStatus.PENDING && (
              <div className="medication-card-row">
                Next Refill: {
                  latestPrescription.status === PrescriptionStatus.PAUSED ? 'None' :
                  !latestPrescription.hasRefill ? 'None' :
                  latestPrescription.daysToRefill ? 
                  moment(latestPrescription.orderedAt || latestPrescription.createdAt).add(latestPrescription.daysToRefill, 'days').format('MM/DD/YYYY') :
                  latestPrescription.contents ? 
                    moment(latestPrescription.orderedAt || latestPrescription.createdAt).add(latestPrescription.contents.units/(latestPrescription.contents.doseQty || 1), 'days').format('MM/DD/YYYY') :
                    moment(latestPrescription.orderedAt || latestPrescription.createdAt).add(90, 'days').format('MM/DD/YYYY')
              }
            </div>
            )}
          </div>
        </Card>

    
      </>
    );
  };

  const MedicationHistoryDrawer = () => {
    if (!selectedMedication) return null;

    // Sort prescriptions by date in descending order (most recent first)
    const sortedHistory = [...selectedMedication.prescriptions].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)  // Changed order of a and b
    );

    return (
      <Drawer
        title={`${selectedMedication.type} History`}
        placement="right"
        onClose={() => setIsMedicationHistoryDrawerVisible(false)}
        visible={isMedicationHistoryDrawerVisible}
        width={600}
        className="medication-history-drawer"
      >
        <Row gutter={[16, 16]}>
          {sortedHistory.map((prescription, index) => (
            <Col key={prescription._id} span={24}>
              <Card key={prescription._id} className="history-card flat">
            <div className="history-card-content">
              <div className="history-card-row">
                <Text strong>Order Date:</Text> {moment(prescription.createdAt).format('MM/DD/YYYY')}
              </div>
              <div className="history-card-row">
                <Text strong>Units:</Text> {prescription.contents?.units}
              </div>
              {prescription.pharmacy && (
                <div className="history-card-row">
                  <Text strong>Pharmacy:</Text> {prescription.pharmacy?.name}
                </div>
              )}
              {prescription.tracking  && (
                <div className="history-card-row">
                  <Text strong>{TrackingHelper.getDeliveryService(prescription.tracking.trackingNumber).service || 'Tracking'}: </Text>
                  <Tooltip 
                    overlayStyle={{ maxWidth: '500px'}}
                    title={ShippingHelper.getImportantEvents(prescription.tracking.trackingEvents)?.length ? (
                      <ul className="tracking-list">
                        {ShippingHelper.getImportantEvents(prescription.tracking.trackingEvents).map(({ eventTimestamp, eventType, eventCity, eventState }, index) => (
                          <li key={`tracking-${prescription._id}-${index}`}>
                            <strong>{moment(eventTimestamp).format('MM/DD/YYYY')}</strong>: {eventType} {(eventCity && eventState) ? ` (${startCase(toLower(eventCity))}, ${eventState})` : ''}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  >

                    <a 
                      className="link" 
                      href={TrackingHelper.getDeliveryService(prescription.tracking.trackingNumber).url} 
                      target="_blank"
                    >
                      {prescription.tracking.trackingNumber}
                    </a>
                  </Tooltip>
                </div>
              )}
                </div>

              {prescription.gogoOrderResponse && !prescription.tracking && moment(prescription.createdAt).isBefore(moment().subtract(1, 'month')) && (
                <div className="history-card-row">
                  <Text strong>Tracking:</Text> Coming Soon
                </div>
              )}
              </Card> 
            </Col>
          ))}
        </Row>
      </Drawer>
    );
  };

  const MedicationDetailsDrawer = () => {
    if (!selectedMedication) return null;
  
    const info = PrescriptionData.find(m => m.types.includes(selectedMedication.type))
    return (
      <Drawer
        title={`${selectedMedication.type} Details`}
        placement="right"
        onClose={() => setIsInfoDrawerVisible(false)}
        visible={isInfoDrawerVisible}
        width={600}
      >
        {info ? (
          <div className="medication-details">
            {info.warnings && (
              <div className="medication-details-section warning-section">
                <Text className="medication-details-section-title">
                  <WarningOutlined /> Warnings
                </Text>
                <ul className="warning-list">
                  {info.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="medication-details-section">
              <Text className="medication-details-section-title">
                <MedicineBoxOutlined /> How to Use
              </Text>
              <div className="medication-details-content">
                {Array.isArray(info.howToUse) ? info.howToUse.map((step, index) => (
                  <ul key={index}>
                    <li>{step}</li>
                  </ul>
                )) : info.howToUse}
              </div>
            </div>

            <div className="medication-details-section">
              <Text className="medication-details-section-title">
                <AlertOutlined /> Side Effects
              </Text>
              <div className="medication-details-content">
                {Array.isArray(info.sideEffects) ? info.sideEffects.map((effect, index) => (
                  <ul key={index}>
                    <li>{effect}</li>
                  </ul>
                )) : info.sideEffects}
              </div>
            </div>

            <div className="medication-details-section">
              <Text className="medication-details-section-title">
                <LinkOutlined /> Interactions
              </Text>
              <div className="medication-details-content">
                {Array.isArray(info.interactions) ? info.interactions.map((interaction, index) => (
                  <ul key={index}>
                    <li>{interaction}</li>
                  </ul>
                )) : info.interactions}
              </div>
            </div>

            <div className="medication-details-section">
              <Text className="medication-details-section-title">
                <SafetyOutlined /> Precautions
              </Text>
              <div className="medication-details-content">
                {Array.isArray(info.precautions) ? info.precautions.map((precaution, index) => (
                  <ul key={index}>
                    <li>{precaution}</li>
                  </ul>
                )) : info.precautions}
              </div>
            </div>

            <div className="medication-details-section">
              <Text className="medication-details-section-title">
              <InboxOutlined />  Storage
              </Text>
              <div className="medication-details-content">
                {Array.isArray(info.storage) ? info.storage.map((storage, index) => (
                  <ul key={index}>
                    <li>{storage}</li>
                  </ul>
                )) : info.storage}
              </div>
            </div>

            <div className="medication-details-section">
              <Text className="medication-details-section-title">
                <ExclamationCircleOutlined /> Overdose
              </Text>
              <div className="medication-details-content">
                {Array.isArray(info.overDose) ? info.overDose.map((overdose, index) => (
                  <ul key={index}>
                    <li>{overdose}</li>
                  </ul>
                )) : info.overDose}
              </div>
            </div>

      
          </div>
        ) : (
          <div className="medication-details coming-soon">
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Medication details coming soon"
            />
          </div>
        )}
      </Drawer>
    );
  };

  const StopMedicationModal = () => {
    if (!selectedMedication) return null;

    const handleStopMedication = async () => {
      const latestPrescription = selectedMedication.prescriptions[selectedMedication.prescriptions.length - 1];
      const response = await cancelPrescription(latestPrescription._id);
      
      if (response.success) {
        // Update the local state to reflect the change
        const updatedPrescriptions = prescriptions.map(prescription => {
          if (prescription._id === latestPrescription._id) {
            return {
              ...prescription,
              status: PrescriptionStatus.PAUSED,
            };
          }
          return prescription;
        });

        setPrescriptions(updatedPrescriptions)

        // Update the grouped prescriptions state
        groupedPrescriptions[selectedMedication.type] = updatedPrescriptions;

        setIsStopModalVisible(false);
        setSelectedMedication(null);
        message.success('Prescription stopped successfully.');
      } else {
        message.error('Failed to stop prescription. Please try again.');
      }
    };

    return (
      <Modal
        title={`Are you sure you want to stop ${selectedMedication.type}?`}
        open={isStopModalVisible}
        onOk={handleStopMedication}
        onCancel={() => {
          setIsStopModalVisible(false);
          setSelectedMedication(null);
        }}
        okText="Yes, stop it"
        cancelText="No, keep it"
        okButtonProps={{ 
          danger: true
        }}
        okType="default"
      >
        <div className="stop-medication-modal">

          <div className="consequences-section">
            <h4>If you stop this medication:</h4>
            <ul>
              <li>
                <StopOutlined /> You will no longer receive refills
              </li>
              <li>
                <StopOutlined /> Your current prescription will be marked as inactive
              </li>
              <li>
                <ReloadOutlined /> You can restart it later, but the approval process will be required again
              </li>
            </ul>
          </div>

          <div className="health-warning">
            <InfoCircleOutlined className="info-icon" />
            <p>Stopping this medication may affect your health. Please consult your healthcare provider if you're unsure.</p>
          </div>
        </div>
      </Modal>
    );
  };

  const renderGetTreatmentDropdown = () => {
      const menu = (
    <Menu>
      <Menu.Item key="2" onClick={() => {navigate('/heart')}}>
        Heart Health Program
      </Menu.Item>
      <Menu.Item key="1" onClick={() => {navigate('/treatment/low-dose-statin')}}>
        Low-Dose Statin
      </Menu.Item>
      <Menu.Item key="3" onClick={() => {navigate('/treatment/ezetimibe')}}>
        Ezetimibe 
      </Menu.Item>
    </Menu>
  );
    return (
      <Dropdown overlay={menu} trigger={['click']}>
        <Button type="primary" icon={<PlusOutlined />}>
          Get Treatment <DownOutlined />
        </Button>
      </Dropdown>
    );
  }

  return (
    <div className="prescriptions">
      <PageHeader
        title='Prescriptions'
        actions={renderGetTreatmentDropdown()}  
      />

      <div className="prescriptions-container">
        {isLoading ? (
          <div className="loading-container"><Spin size="large" /><p>One Moment Please<br/>Loading Prescriptions</p></div>
        ) : (
          <Row gutter={[16, 16]}>
            {Object.entries(groupedPrescriptions)
              .sort(([typeA, prescriptionsA], [typeB, prescriptionsB]) => {
                // Get latest prescription for each type
                const latestA = [...prescriptionsA].sort((a, b) => 
                  new Date(b.createdAt) - new Date(a.createdAt)
                )[0];
                const latestB = [...prescriptionsB].sort((a, b) => 
                  new Date(b.createdAt) - new Date(a.createdAt)
                )[0];
                
                // If either is paused, sort it to the bottom
                if (latestA.status === PrescriptionStatus.PAUSED && latestB.status !== PrescriptionStatus.PAUSED) return 1;
                if (latestA.status !== PrescriptionStatus.PAUSED && latestB.status === PrescriptionStatus.PAUSED) return -1;
                
                // If both are paused or both are active, maintain original order
                return 0;
              })
              .map(([type, prescriptions]) => (
                <Col key={type} xs={24}  md={12} lg={8}>
                  <MedicationCard type={type} prescriptions={prescriptions} />
                </Col>
              ))}
          </Row>
        )}
      </div>

      <MedicationHistoryDrawer />
      <MedicationDetailsDrawer />
      <StopMedicationModal />
    </div>
  )
}